import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import useGlynt from './useGlynt';

type ParamTypes = {
  trainingSetId: string;
  limit: number;
  defaultPage: number;
};

const MAX_POLL_COUNT = 30; // Stop polling after 30 calls (2.5 minutes at 5s intervals)

export default function usePaginatedTrainingRevisions({
  trainingSetId,
  limit = 100,
  defaultPage = 0,
}: ParamTypes) {
  const [page, setPage] = useState(defaultPage);
  const [isPolling, setIsPolling] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const refetchIntervalMs = 5000; // every 5 seconds when training in progress
  const glynt = useGlynt();

  const { data, isError, isPending, isFetching, refetch } = useQuery({
    queryKey: ['trainingRevisions', { page, limit, trainingSetId }],

    queryFn: async () =>
      (await glynt).trainingRevision.list({
        advanced: true,
        ordering: '-created_at',
        limit,
        offset: page * limit,
        training_set: trainingSetId,
      }),

    refetchInterval: isPolling && pollCount < MAX_POLL_COUNT ? refetchIntervalMs : false
  });

  useEffect(() => {
    const hasInProgress =
      data &&
      data.results.findIndex((tr) => tr.status === 'In Progress' || tr.status === 'Pending') > -1;

    if (hasInProgress) {
      setIsPolling(true);
      setPollCount((prev) => prev + 1);
    } else {
      setIsPolling(false);
      setPollCount(0); // Reset count when no longer in progress
    }
  }, [data]);

  const total = data ? Math.ceil(data.count / limit) : 1;

  const nextPage = () => {
    if (page < total - 1) setPage((page) => page + 1);
  };

  const previousPage = () => {
    if (page > 0) setPage((page) => page - 1);
  };

  return {
    data,
    isError,
    isPending,
    isLoading: isPending,
    nextPage,
    previousPage,
    total,
    page,
    setPage,
    isFetching,
    refetch,
  };
}
